// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { TriangleAlert } from "lucide-react"
import { Button } from "@/components/ui/button"
import ebioseMarkMint from "@/assets/brand/ebiose_mark_mint.svg"

/**
 * The non-tabular renderings of `/forges`: the placeholder for an absent
 * value, and the three states where there is no table to show (loading,
 * error, and the two kinds of emptiness). Each renders inside the same
 * table shell as the populated list, per the frozen design.
 */

export const Dash = () => <span className="text-text-muted">—</span>

/** The rounded, hairline-bordered container every state shares with the table. */
const shellClass =
  "overflow-hidden rounded-xl border border-border bg-table-surface"

/**
 * Header labels of the default-visible columns, echoed by the skeleton
 * with the populated table's fractional widths (columns.tsx `widthFr`).
 */
const SKELETON_HEADERS: Array<{ label: string; fr: number; right?: boolean }> =
  [
    { label: "Forge", fr: 1.8 },
    { label: "Status", fr: 1.3 },
    { label: "Best score", fr: 0.95 },
    { label: "Agents", fr: 0.65, right: true },
    { label: "Cycles", fr: 0.65, right: true },
    { label: "Cost", fr: 0.8, right: true },
    { label: "Last cycle", fr: 1.05 },
    { label: "Created", fr: 1.1 },
  ]

const SKELETON_BAR_WIDTHS = ["42%", "14%", "10%", "18%", "22%"]

const shimmer =
  "h-[11px] rounded-md animate-shimmer bg-[linear-gradient(90deg,rgba(255,255,255,0.04),rgba(255,255,255,0.1),rgba(255,255,255,0.04))] [background-size:200%_100%]"

export const ForgesSkeleton = () => (
  <div role="status" aria-label="Loading forges" className={shellClass}>
    <div className="flex items-center gap-3 border-b border-table-header-line bg-table-header px-[18px] py-[11px]">
      {SKELETON_HEADERS.map(({ label, fr, right }) => (
        <span
          key={label}
          style={{ flexGrow: fr, flexBasis: 0 }}
          className={`truncate font-mono text-[10.5px] tracking-[0.05em] text-text-tertiary uppercase ${right ? "text-right" : ""}`}
        >
          {label}
        </span>
      ))}
    </div>
    {Array.from({ length: 8 }, (_, row) => (
      <div
        key={row}
        className="flex items-center gap-3.5 border-b border-table-row-line px-[18px] py-[15px] last:border-b-0"
      >
        {SKELETON_BAR_WIDTHS.map((width) => (
          <span key={width} className={shimmer} style={{ width }} />
        ))}
      </div>
    ))}
  </div>
)

/**
 * Zero forges — the onboarding state. Forges are born from the SDK, so
 * the page teaches the real gesture instead of offering a create CTA the
 * product does not have.
 */
export const ForgesOnboarding = () => (
  <div className={shellClass}>
    <div className="flex flex-col items-center gap-3.5 px-10 py-16 text-center">
      <img
        src={ebioseMarkMint}
        alt=""
        className="h-[52px] opacity-90 [filter:drop-shadow(0_0_14px_rgba(69,240,200,0.4))]"
      />
      <h2 className="mt-1.5 text-lg font-semibold text-foreground">
        No forges yet.
      </h2>
      <p className="max-w-[440px] text-[13.5px] leading-relaxed text-text-secondary">
        Forges appear here automatically once you launch your first evolution
        cycle from the Ebiose Python SDK. Define an objective, run a cycle, and
        your forge shows up with its live status and best score.
      </p>
    </div>
  </div>
)

/**
 * Zero *matches* under an active search — distinct from the onboarding
 * state above: a typo must never tell a user they own no forge.
 */
export const NoForgesMatch = ({
  query,
  onClear,
}: {
  query: string
  onClear: () => void
}) => (
  <div className={shellClass}>
    <div className="flex flex-col items-center gap-3 px-10 py-14 text-center">
      <h2 className="text-lg font-semibold text-foreground">
        No forges match “{query}”.
      </h2>
      <Button variant="outline" type="button" onClick={onClear}>
        Clear search
      </Button>
    </div>
  </div>
)

export const ForgesError = ({ onRetry }: { onRetry: () => void }) => (
  <div role="alert" className={shellClass}>
    <div className="flex flex-col items-center gap-3 px-10 py-14 text-center">
      <span className="flex size-11 items-center justify-center rounded-full border border-[rgba(255,107,107,0.35)] bg-[rgba(255,107,107,0.12)] text-failed-fg">
        <TriangleAlert aria-hidden="true" className="size-[22px]" />
      </span>
      <h2 className="text-lg font-semibold text-failed-fg">
        Could not load your forges.
      </h2>
      <p className="max-w-[440px] text-[13.5px] leading-relaxed text-text-secondary">
        Something went wrong while fetching your workspace. Check your
        connection and try again.
      </p>
      <Button variant="outline" type="button" onClick={onRetry}>
        Retry
      </Button>
    </div>
  </div>
)
